import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Upload, Download, SlidersHorizontal, Bookmark, Mail, MessageCircle, Tag, Trash2, X, Search, Users } from 'lucide-react';
import { toast } from 'sonner';
import { TABS, FILTER_LABELS, cleanFilters } from '@/lib/buyersV2';
import { BuyerTableV2 } from '@/components/buyersV2/BuyerTableV2';
import { FilterPanel } from '@/components/buyersV2/FilterPanel';
import { ImportWizard } from '@/components/buyersV2/ImportWizard';
import { SaveSegmentDialog, SegmentsTab, ImportHistoryTab } from '@/components/buyersV2/SegmentsAndImports';
import { AddEntityDialog } from '@/components/buyersV2/AddEntityDialog';
import { CampaignsV2Tab } from '@/components/buyersV2/CampaignsV2Tab';
import { NewCampaignV2Dialog } from '@/components/buyersV2/NewCampaignV2Dialog';
import { PropertyMatchBar } from '@/components/buyersV2/PropertyMatchBar';
import { DedupeDialog } from '@/components/buyersV2/DedupeDialog';
import { TagDialog } from '@/components/buyers/GroupSettingsDialog';

const EMPTY = { items: [], total: 0, page: 1, pages: 1 };

function Chips({ filters, onRemove }) {
  const keys = Object.keys(filters).filter(k => !['sort_by', 'sort_order', 'q'].includes(k));
  if (!keys.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="active-filters">
      {keys.map(k => (
        <span key={k} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-50 text-[#0073ea] border border-blue-100">
          {FILTER_LABELS[k] || k}: {filters[k] === 'true' ? 'yes' : filters[k]}
          <button onClick={() => onRemove(k)} data-testid={`remove-filter-${k}`}><X className="h-3 w-3" /></button>
        </span>
      ))}
    </div>
  );
}

function BulkBar({ count, on }) {
  return (
    <div data-testid="bulk-action-bar" className="sticky top-2 z-20 flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-900 text-white shadow-lg">
      <span className="text-sm font-medium mr-2"><span data-testid="selected-count">{count}</span> selected</span>
      <Button size="sm" data-testid="bulk-campaign-btn" onClick={on.campaign} className="bg-white text-gray-900 hover:bg-gray-100"><Mail className="h-3.5 w-3.5 mr-1" /> Campaign</Button>
      <Button size="sm" data-testid="bulk-email-btn" onClick={on.email} className="bg-[#0073ea] hover:bg-[#0060c2]"><Mail className="h-3.5 w-3.5 mr-1" /> Email</Button>
      <Button size="sm" data-testid="bulk-text-btn" onClick={on.text} className="bg-[#25D366] hover:bg-[#1ebe5b]"><MessageCircle className="h-3.5 w-3.5 mr-1" /> Text</Button>
      <Button size="sm" data-testid="bulk-tag-btn" onClick={on.tag} variant="secondary"><Tag className="h-3.5 w-3.5 mr-1" /> Tag</Button>
      <Button size="sm" data-testid="bulk-delete-btn" onClick={on.del} variant="ghost" className="text-red-300 hover:text-red-200 hover:bg-white/10"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
      <button onClick={on.clear} className="ml-auto text-white/60 hover:text-white" data-testid="clear-selection"><X className="h-4 w-4" /></button>
    </div>
  );
}

export default function BuyersPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ by: '', order: 'desc' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(EMPTY);
  const [tags, setTags] = useState([]);
  const [selected, setSelected] = useState({});
  const [dialog, setDialog] = useState(null);
  const [importKey, setImportKey] = useState(0);
  const tabDef = TABS.find(t => t.key === tab);
  const isList = !!tabDef.filters;
  const effective = cleanFilters({ ...(tabDef.filters || {}), ...filters, sort_by: sort.by, sort_order: sort.by ? sort.order : '' });

  const load = useCallback(async () => {
    if (!isList) return;
    try { setData((await API.get('/buyers', { params: { ...effective, page, page_size: 50, v2: 1 } })).data); }
    catch { toast.error('Failed to load buyers'); }
  }, [JSON.stringify(effective), page, isList]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { API.get('/buyers/stats').then(r => setTags(r.data.tags || [])).catch(() => {}); }, [importKey]);
  useEffect(() => { setPage(1); }, [JSON.stringify(filters), tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSort = (by) => setSort(p => ({ by, order: p.by === by && p.order === 'desc' ? 'asc' : by === 'entity_name' && p.by !== by ? 'asc' : 'desc' }));
  const toggle = (b) => setSelected(p => { const n = { ...p }; n[b.id] ? delete n[b.id] : (n[b.id] = b); return n; });
  const toggleAll = () => setSelected(p => { const all = data.items.every(b => p[b.id]); const n = { ...p }; data.items.forEach(b => all ? delete n[b.id] : (n[b.id] = b)); return n; });
  const selIds = Object.keys(selected);
  const close = (v) => !v && setDialog(null);
  const refresh = () => { load(); setImportKey(k => k + 1); };

  const exportCsv = async () => {
    const res = await API.get('/buyers/export', { params: { ...effective, v2: 1 }, responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([res.data])); a.download = 'buyers_export.csv'; a.click();
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selIds.length} buyer(s) with their contacts and purchases?`)) return;
    await Promise.all(selIds.map(id => API.delete(`/buyers/${id}`).catch(() => null)));
    toast.success('Deleted'); setSelected({}); load();
  };
  const openSegment = (s) => {
    const { sort_by, sort_order, ...rest } = s.filters || {};
    setTab('all'); setFilters(rest); setSort({ by: sort_by || '', order: sort_order || 'desc' });
  };

  return (
    <div data-testid="buyers-page" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-2xl font-bold text-gray-900">Cash Buyers</h1><p className="text-sm text-gray-500">Entities, contacts and every purchase — sorted by where they buy</p></div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="dedupe-btn" onClick={() => setDialog('dedupe')}>Find Duplicates</Button>
          <Button variant="outline" data-testid="import-btn" onClick={() => setDialog('import')}><Upload className="h-4 w-4 mr-1.5" /> Import</Button>
          <Button data-testid="add-buyer-btn" onClick={() => setDialog('add')} className="bg-[#0073ea] hover:bg-[#0060c2] text-white"><Plus className="h-4 w-4 mr-1.5" /> Add Buyer</Button>
        </div>
      </div>

      <PropertyMatchBar />

      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} data-testid={`buyers-tab-${t.key}`} onClick={() => { setTab(t.key); if (t.filters) setFilters({}); }}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.key ? 'border-[#0073ea] text-[#0073ea]' : 'border-transparent text-gray-500 hover:text-gray-900'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'segments' && <SegmentsTab onOpenSegment={openSegment} />}
      {tab === 'campaigns' && <CampaignsV2Tab />}
      {tab === 'imports' && <ImportHistoryTab refreshKey={importKey} />}

      {isList && (<>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input data-testid="buyer-search" value={filters.q || ''} onChange={e => setFilters(p => cleanFilters({ ...p, q: e.target.value }))} placeholder="Search entity, doc #, phone, email, tag…" className="pl-9 bg-white border-gray-300" />
          </div>
          <Button variant="outline" data-testid="open-filters-btn" onClick={() => setDialog('filters')}><SlidersHorizontal className="h-4 w-4 mr-1.5" /> Filters</Button>
          <Button variant="outline" data-testid="save-segment-btn" onClick={() => setDialog('segment')}><Bookmark className="h-4 w-4 mr-1.5" /> Save Segment</Button>
          <Button variant="outline" data-testid="export-btn" onClick={exportCsv}><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
        </div>
        <Chips filters={filters} onRemove={k => setFilters(p => { const n = { ...p }; delete n[k]; return n; })} />
        {selIds.length > 0 && <BulkBar count={selIds.length} on={{ campaign: () => setDialog('campaign'), email: () => setDialog('campaign-email'), text: () => setDialog('campaign-sms'), tag: () => setDialog('tag'), del: bulkDelete, clear: () => setSelected({}) }} />}
        {data.items.length === 0 ? (
          <div className="text-center py-16" data-testid="no-buyers"><Users className="h-12 w-12 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">No buyers match. Import a CSV/XLSX to build your database.</p></div>
        ) : (
          <BuyerTableV2 data={data} sort={{ by: sort.by || data.sort_by, order: sort.order }} onSort={onSort} selected={selected} onToggle={toggle} onToggleAll={toggleAll}
            onOpen={b => navigate(`/buyers/${b.id}`)} showLoc={!!(filters.zip || filters.city)} page={page} setPage={setPage} />
        )}
      </>)}

      <FilterPanel open={dialog === 'filters'} onOpenChange={close} filters={filters} onApply={f => setFilters(cleanFilters(f))} tags={tags} />
      <SaveSegmentDialog open={dialog === 'segment'} onOpenChange={close} filters={cleanFilters({ ...(tabDef.filters || {}), ...filters, sort_by: sort.by, sort_order: sort.by ? sort.order : '' })} />
      <ImportWizard open={dialog === 'import'} onOpenChange={close} onDone={refresh} />
      <AddEntityDialog open={dialog === 'add'} onOpenChange={close} onCreated={(id) => id ? navigate(`/buyers/${id}`) : load()} />
      <NewCampaignV2Dialog open={['campaign', 'campaign-email', 'campaign-sms'].includes(dialog)} onOpenChange={close} preset={{ buyer_ids: selIds, channel: dialog === 'campaign-sms' ? 'sms' : 'email' }} />
      <DedupeDialog open={dialog === 'dedupe'} onOpenChange={close} onMerged={refresh} />
      <TagDialog open={dialog === 'tag'} onOpenChange={close} buyerIds={selIds} onSaved={load} />
    </div>
  );
}
